import { createHash } from 'node:crypto';
import { z } from 'zod';
import { Id } from './domain.js';
import { editDistance } from './objective.js';

/**
 * The improvement loop. Everything the daily improver may change lives in a versioned Playbook.
 * Safety rules, the claims register and "never publish" live in code and are NOT part of the playbook.
 *
 * Objective: true to the message (hard gate), then highest reach and engagement.
 */

// ---------- playbook ----------

/**
 * LLM steps whose prompt the playbook can extend. Must match `@reelmachine/llm` step names.
 * The understanding check and the claims check are judges of fidelity and are NOT editable: a playbook
 * must never be able to grade itself. `analyze` builds the claims register, so changes to it are high risk.
 */
export const PlaybookStep = z.enum([
  'analyze',
  'condense',
  'narrative',
  'profile',
  'concepts',
  'panel',
  'writeScript',
  'chat',
  'caption',
  'channel',
]);
export type PlaybookStep = z.infer<typeof PlaybookStep>;

export const NarrativeId = z.enum(['E1', 'E2', 'E3', 'E4', 'E5', 'E6', 'E7']);

export const PlaybookRule = z.object({
  id: z.string().regex(/^[a-z0-9-]{3,60}$/),
  /** One clear instruction for the model. Plain language, max 400 chars. */
  text: z.string().min(10).max(400),
  /** Steps the rule is shown to. */
  steps: z.array(PlaybookStep).min(1),
  /** "testing" rules are applied and watched; a person or the guard promotes them to "active". */
  status: z.enum(['active', 'testing']),
  /** Why the rule exists: evidence summary, reel ids, experiment id. */
  evidence: z.string().max(1000).default(''),
  since: z.string(),
});
export type PlaybookRule = z.infer<typeof PlaybookRule>;

const Shift = z.number().min(-2).max(2);
/** One few-shot example: what went in (short) and the output we want. Material, not instructions. */
export const FewShot = z.object({
  input: z.string().max(600).default(''),
  output: z.string().min(1).max(600),
  /** Where it comes from, e.g. "reel_x, 2.4x channel median". */
  source: z.string().max(200).default(''),
});
export type FewShot = z.infer<typeof FewShot>;
/** Bedrock model or inference profile id, e.g. "eu.anthropic.claude-...". */
const ModelId = z.string().regex(/^[a-z0-9][a-z0-9.:-]{2,120}$/);
/** Effect catalog ids, e.g. "snap-zoom". */
const EffectId = z.string().regex(/^[a-z0-9-]{2,40}$/);

export const PlaybookParams = z.object({
  /** Extra guidance appended to a step's prompt, inside the editable slot. Max 2000 chars per step. */
  addenda: z.record(PlaybookStep, z.string().max(2000)).default({}),
  rules: z.array(PlaybookRule).max(40).default([]),
  /** Points added to a narrative's score in the narrative choice (-3..3). */
  narrativeBias: z.record(NarrativeId, z.number().min(-3).max(3)).default({}),
  /** Added to the computed video profile (Energie, Spiel, Nähe, Grafik), clamped to 0..10. */
  profileShift: z.tuple([Shift, Shift, Shift, Shift]).default([0, 0, 0, 0]),
  /** Preference weights for hook types (0..2, 1 = neutral) used when ranking hooks. */
  hookTypeWeights: z
    .record(z.string().regex(/^[\p{L}][\p{L} -]{1,39}$/u), z.number().min(0).max(2))
    .refine((r) => Object.keys(r).length <= 12, 'At most 12 hook types')
    .default({}),
  effects: z
    .object({
      prefer: z.array(EffectId).max(20).default([]),
      avoid: z.array(EffectId).max(20).default([]),
    })
    .default({}),
  /** Target length range in seconds; falls back to the channel profile. */
  lengthS: z.tuple([z.number().min(10).max(90), z.number().min(10).max(90)]).optional(),
  panel: z
    .object({
      judgeVariant: z.enum(['V1', 'V2']).default('V2'),
      /** "decide" only when the judge is calibrated (holdout >= 0.65 and beats baselines). */
      mode: z.enum(['decide', 'filter']).default('filter'),
    })
    .default({}),
  /**
   * Few-shot examples per step (Ng lever 2), e.g. the best-performing hooks of real reels for `concepts`.
   * Shown inside the playbook slot as examples, never as instructions.
   */
  examples: z.record(PlaybookStep, z.array(FewShot).max(5)).default({}),
  /** Model per step (Ng lever 3). Unset steps use the default model. Ids are Bedrock model or profile ids. */
  models: z.record(PlaybookStep, ModelId).default({}),
  /**
   * Pipeline granularity (Ng: start coarse, split only when needed). "fine" runs every step of the skill,
   * "coarse" merges analyze+condense+narrative+profile and concepts+writeScript into two calls.
   */
  pipelineShape: z.enum(['fine', 'coarse']).default('fine'),
});
export type PlaybookParams = z.infer<typeof PlaybookParams>;

/** A partial update of the params. Arrays and records replace the whole field. */
export const PlaybookPatch = PlaybookParams.partial();
export type PlaybookPatch = z.infer<typeof PlaybookPatch>;

export const ChangeActor = z.object({ type: z.enum(['user', 'improver', 'system']), id: z.string(), name: z.string() });

export const PlaybookVersion = z.object({
  /** "global" or an org id (org overrides merge over global). */
  scope: z.string(),
  version: z.number().int().positive(),
  params: PlaybookParams,
  parentVersion: z.number().int().positive().optional(),
  status: z.enum(['active', 'candidate', 'retired']),
  /** How it got live: direct apply after eval, experiment win, human decision, rollback, seed. */
  origin: z.enum(['seed', 'direct', 'experiment', 'decision', 'rollback', 'manual']),
  note: z.string().max(2000),
  createdBy: ChangeActor,
  createdAt: z.string(),
  /** Set while the guard watches a directly applied change. */
  watch: z.object({ untilReels: z.number().int().positive(), baseline: z.number(), startedAt: z.string() }).optional(),
});
export type PlaybookVersion = z.infer<typeof PlaybookVersion>;

export const EMPTY_PARAMS: PlaybookParams = PlaybookParams.parse({});

/** Global params, then org overrides. Rules merge by id (org wins), records merge by key. */
export function mergeParams(global: PlaybookParams, org?: PlaybookPatch): PlaybookParams {
  if (!org) return global;
  const rules = new Map(global.rules.map((r) => [r.id, r]));
  for (const r of org.rules ?? []) rules.set(r.id, r);
  const addenda: PlaybookParams['addenda'] = { ...global.addenda };
  for (const [k, v] of Object.entries(org.addenda ?? {})) {
    const step = k as PlaybookStep;
    addenda[step] = [global.addenda[step], v].filter(Boolean).join('\n');
  }
  return PlaybookParams.parse({
    ...global,
    ...org,
    addenda,
    rules: [...rules.values()],
    narrativeBias: { ...global.narrativeBias, ...org.narrativeBias },
    hookTypeWeights: { ...global.hookTypeWeights, ...org.hookTypeWeights },
    effects: org.effects ?? global.effects,
    panel: { ...global.panel, ...org.panel },
    examples: { ...global.examples, ...org.examples },
    models: { ...global.models, ...org.models },
    pipelineShape: org.pipelineShape ?? global.pipelineShape,
  });
}

export function applyPlaybookPatch(params: PlaybookParams, patch: PlaybookPatch): PlaybookParams {
  return PlaybookParams.parse({ ...params, ...PlaybookPatch.parse(patch) });
}

/** What changed between two param sets, as field names. Used for risk classes and changelogs. */
export function changedFields(a: PlaybookParams, b: PlaybookParams): string[] {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof PlaybookParams>;
  return [...keys].filter((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k])).sort();
}

/**
 * Risk of a change. "low" may go live directly after a passed offline eval (decision 2026-11-01: yes).
 * "medium" runs as an experiment. "high" becomes a decision for a person.
 */
export type ChangeRisk = 'low' | 'medium' | 'high';

export function changeRisk(before: PlaybookParams, after: PlaybookParams): ChangeRisk {
  const fields = changedFields(before, after);
  if (fields.length === 0) return 'low';
  if (fields.includes('panel') || fields.includes('lengthS')) return 'high';
  // models and pipeline shape change behaviour broadly: only as an experiment, never directly
  if (fields.includes('models') || fields.includes('pipelineShape')) return 'medium';
  if (JSON.stringify(before.examples.analyze ?? []) !== JSON.stringify(after.examples.analyze ?? [])) return 'high';
  // analyze builds the claims register, the basis of the fidelity checks
  if ((before.addenda.analyze ?? '') !== (after.addenda.analyze ?? '')) return 'high';
  if (rulesTouchStep(before, after, 'analyze')) return 'high';
  const steps = new Set([...Object.keys(before.addenda), ...Object.keys(after.addenda)]) as Set<PlaybookStep>;
  let addendaEdit = 0;
  for (const st of steps) addendaEdit += editDistance(before.addenda[st] ?? '', after.addenda[st] ?? '');
  const beforeRules = new Map(before.rules.map((r) => [r.id, r]));
  const afterIds = new Set(after.rules.map((r) => r.id));
  let rulesChanged = before.rules.filter((r) => !afterIds.has(r.id)).length;
  for (const r of after.rules) {
    const old = beforeRules.get(r.id);
    if (!old || old.text !== r.text || old.status !== r.status || old.steps.join() !== r.steps.join()) rulesChanged++;
  }
  const biasMoves = Object.values(after.narrativeBias).filter((v) => Math.abs(v) >= 2).length;
  const profileMove = after.profileShift.some((v, i) => Math.abs(v - before.profileShift[i]!) > 1);
  const exampleSteps = new Set([...Object.keys(before.examples), ...Object.keys(after.examples)]) as Set<PlaybookStep>;
  let examplesChanged = 0;
  for (const st of exampleSteps) {
    const a = new Set((before.examples[st] ?? []).map((e) => e.output));
    for (const e of after.examples[st] ?? []) if (!a.has(e.output)) examplesChanged++;
  }
  if (fields.length > 2 || rulesChanged > 2 || addendaEdit > 800 || biasMoves > 0 || profileMove || examplesChanged > 3)
    return 'medium';
  return 'low';
}

function rulesTouchStep(a: PlaybookParams, b: PlaybookParams, step: PlaybookStep): boolean {
  const pick = (p: PlaybookParams) =>
    JSON.stringify(p.rules.filter((r) => r.steps.includes(step)).sort((x, y) => x.id.localeCompare(y.id)));
  return pick(a) !== pick(b);
}

/**
 * Rules whose text or steps changed (or that are new) start as "testing", whatever the patch says.
 * Only a person promotes them to "active".
 */
export function demoteChangedRules(before: PlaybookParams, after: PlaybookParams): PlaybookParams {
  const old = new Map(before.rules.map((r) => [r.id, r]));
  return {
    ...after,
    rules: after.rules.map((r) => {
      const o = old.get(r.id);
      const same = o && o.text === r.text && o.steps.join() === r.steps.join() && o.status === r.status;
      return same ? r : { ...r, status: 'testing' as const };
    }),
  };
}

/**
 * Which playbook produced a reel. `served` is the version whose params the pipeline used: the global
 * version, the org version (org overrides merged over `global`), or an experiment arm version.
 */
export const PlaybookRef = z.object({
  global: z.number().int().positive(),
  org: z.number().int().positive().optional(),
  served: z.object({ scope: z.string(), version: z.number().int().positive() }),
  experimentId: z.string().optional(),
  arm: z.enum(['control', 'treatment']).optional(),
});
export type PlaybookRef = z.infer<typeof PlaybookRef>;

// ---------- experiments ----------

export const Arm = z.enum(['control', 'treatment']);
export type Arm = z.infer<typeof Arm>;

export const Experiment = z.object({
  id: Id,
  scope: z.string(),
  hypothesis: z.string().max(1000),
  controlVersion: z.number().int().positive(),
  treatmentVersion: z.number().int().positive(),
  /** Share of new reels that get the treatment. */
  allocation: z.number().min(0.05).max(0.5),
  status: z.enum(['running', 'won', 'lost', 'inconclusive', 'stopped']),
  minPerArm: z.number().int().min(3).default(8),
  maxDays: z.number().int().min(7).max(90).default(42),
  startedAt: z.string(),
  endedAt: z.string().optional(),
  createdBy: ChangeActor,
  /** Latest evaluation, written by the improver. */
  result: z
    .object({
      n: z.object({ control: z.number().int(), treatment: z.number().int() }),
      meanObjective: z.object({ control: z.number(), treatment: z.number() }),
      probTreatmentBetter: z.number().min(0).max(1),
      fidelityOk: z.boolean(),
      evaluatedAt: z.string(),
      note: z.string().default(''),
    })
    .optional(),
});
export type Experiment = z.infer<typeof Experiment>;

/**
 * Permuted-block assignment within a channel (review F11): for every block of `blockSize` reels of one
 * org in one experiment, exactly round(allocation * blockSize) get the treatment, in a deterministic
 * random order. `index` is the reel's 0-based position among that org's reels in the experiment
 * (Store.nextCounter). Balanced per channel even with few reels, reproducible, logged before editing.
 */
export function assignArmBlocked(
  experiment: Pick<Experiment, 'id' | 'allocation'>,
  orgId: string,
  index: number,
  blockSize = 10,
): Arm {
  const block = Math.floor(index / blockSize);
  const pos = index % blockSize;
  const treated = Math.max(1, Math.round(experiment.allocation * blockSize));
  // deterministic shuffle of [0..blockSize) seeded by experiment, org and block
  const order = Array.from({ length: blockSize }, (_, i) => i);
  let seed = createHash('sha256').update(`${experiment.id}:${orgId}:${block}`).digest().readUInt32BE(0);
  for (let i = blockSize - 1; i > 0; i--) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const j = seed % (i + 1);
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  return order.indexOf(pos) < treated ? 'treatment' : 'control';
}

/** Deterministic assignment: the same reel always lands in the same arm. Prefer assignArmBlocked. */
export function assignArm(reelId: string, experiment: Pick<Experiment, 'id' | 'allocation'>): Arm {
  const h = createHash('sha256').update(`${experiment.id}:${reelId}`).digest();
  const u = h.readUInt32BE(0) / 0xffffffff;
  return u < experiment.allocation ? 'treatment' : 'control';
}

// ---------- traces and outcomes ----------

/** One LLM call. Bodies live in S3 (`traces/<reelId|orgId>/<runId>/<id>.json`), the index in the table. */
export const LlmTrace = z.object({
  id: Id,
  orgId: Id,
  reelId: z.string().optional(),
  runId: z.string(),
  step: z.string(),
  model: z.string(),
  playbookVersion: z.number().int().positive().optional(),
  bodyKey: z.string(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  latencyMs: z.number().int().nonnegative(),
  retries: z.number().int().nonnegative(),
  ok: z.boolean(),
  error: z.string().optional(),
  at: z.string(),
});
export type LlmTrace = z.infer<typeof LlmTrace>;

/** Script states kept for edit analysis. */
export const SnapshotLabel = z.enum(['generated', 'approved', 'posted']);
export type SnapshotLabel = z.infer<typeof SnapshotLabel>;

/** One row of daily metrics, kept to see the curve (day 1, 3, 7, 28). */
export const MetricsSnapshot = z.object({
  reelId: Id,
  day: z.number().int().nonnegative(), // days since posting
  views: z.number().int().nonnegative(),
  reach: z.number().int().nonnegative(),
  // Missing means "not reported by the provider", never zero (recheck R11).
  likes: z.number().int().nonnegative().optional(),
  comments: z.number().int().nonnegative().optional(),
  shares: z.number().int().nonnegative().optional(),
  saves: z.number().int().nonnegative().optional(),
  avgWatchPct: z.number().min(0).max(100).optional(),
  at: z.string(),
});
export type MetricsSnapshot = z.infer<typeof MetricsSnapshot>;

/** Everything the improver needs about one posted reel, in one row. Built by the pipeline. */
export const ReelOutcome = z.object({
  reelId: Id,
  orgId: Id,
  postedAt: z.string(),
  playbook: z.lazy(() => PlaybookRef).optional(),
  features: z.object({
    storyType: z.string().optional(),
    narrative: z.string().optional(),
    hookType: z.string().optional(),
    conceptName: z.string().optional(),
    profile: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
    lengthS: z.number().optional(),
    lines: z.number().int().optional(),
    personInFrame0: z.boolean().optional(),
    effects: z.array(z.string()).default([]),
  }),
  edits: z.object({
    charsChanged: z.number().int().nonnegative(),
    editRatio: z.number().min(0),
    hookChanged: z.boolean(),
    chatEdits: z.number().int().nonnegative(),
    regenerations: z.number().int().nonnegative(),
    storylineSwitched: z.boolean(),
    takes: z.number().int().nonnegative(),
  }),
  fidelity: z
    .object({
      coreMean: z.number(),
      passed: z.boolean(),
      claimViolations: z.number().int(),
      /** 2 = yes/no per core statement with neutral judges. Missing = older metric, excluded from comparisons. */
      metricVersion: z.number().int().positive().optional(),
    })
    .optional(),
  metrics: z
    .object({
      day: z.number().int(),
      views: z.number().int(),
      reach: z.number().int(),
      // missing when the provider did not report them (recheck R11): never stored as 0
      engagements: z.number().int().optional(),
      avgWatchPct: z.number().optional(),
      reachIndex: z.number(), // ln(views / channel median)
      engagementRate: z.number().optional(), // engagements / reach; missing when the provider reported no counts
      engagementIndex: z.number().optional(), // smoothed, see objective.ts
      objective: z.number(),
      /** Objective version (objective.ts OBJECTIVE_VERSION). Outcomes of other versions are not compared. */
      objectiveVersion: z.number().int().positive().optional(),
    })
    .optional(),
  updatedAt: z.string(),
});
export type ReelOutcome = z.infer<typeof ReelOutcome>;

// ---------- reports and decisions ----------

export const ImproverAction = z.object({
  kind: z.enum(['applied', 'experiment_started', 'experiment_ended', 'decision_created', 'rolled_back', 'none']),
  ref: z.string().optional(),
  summary: z.string().max(500),
});

export const Report = z.object({
  id: Id,
  date: z.string(), // YYYY-MM-DD
  title: z.string().max(200),
  /** Markdown. Sections: Summary, What worked, What editors change, Metrics, Actions, Open questions. */
  body: z.string().max(40_000),
  actions: z.array(ImproverAction).default([]),
  model: z.string(),
  costUsd: z.number().nonnegative().default(0),
  createdAt: z.string(),
});
export type Report = z.infer<typeof Report>;

export const Decision = z.object({
  id: Id,
  scope: z.string(),
  kind: z.enum(['playbook_change', 'experiment', 'code_change']),
  title: z.string().max(200),
  rationale: z.string().max(4000),
  evidence: z.string().max(4000).default(''),
  /** For playbook changes and experiments. */
  patch: PlaybookPatch.optional(),
  /** For code changes: link to the pull request or issue. */
  link: z.string().optional(),
  status: z.enum(['open', 'approved', 'rejected', 'applied', 'expired']),
  createdAt: z.string(),
  decidedAt: z.string().optional(),
  decidedBy: z.string().optional(),
  reportId: z.string().optional(),
});
export type Decision = z.infer<typeof Decision>;

// ---------- Ng: evals, error analysis, gate ----------

/**
 * Auto-apply gate (Ng: trust metrics only once the evals are good). Direct changes go live automatically
 * only when the gold set has enough approved items and the performance judge is calibrated.
 */
export const AUTO_APPLY_MIN_GOLD = 20;

export interface AutoApplyState {
  approvedGold: number;
  calibration: 'decide' | 'filter' | 'too_little_data' | 'missing';
}

export function autoApplyAllowed(s: AutoApplyState): { allowed: boolean; reason: string } {
  if (s.approvedGold < AUTO_APPLY_MIN_GOLD)
    return { allowed: false, reason: `Gold set has ${s.approvedGold} approved items, needs ${AUTO_APPLY_MIN_GOLD}.` };
  if (s.calibration !== 'decide') return { allowed: false, reason: `Judge calibration is "${s.calibration}".` };
  return { allowed: true, reason: 'Gold set and judge calibration are ready.' };
}

/** Pipeline components that errors are attributed to. Matches the step names plus production. */
export const Component = z.enum([
  'source',
  'analyze',
  'condense',
  'narrative',
  'profile',
  'concepts',
  'panel',
  'writeScript',
  'chat',
  'caption',
  'production',
]);
export type Component = z.infer<typeof Component>;

/** A person's verdict on one step of one reel (weekly error analysis). */
export const ErrorLabel = z.object({
  id: Id,
  reelId: Id,
  component: Component,
  /** "error": worse than an expert given the same input. "upstream": bad only because its input was bad. */
  verdict: z.enum(['ok', 'error', 'upstream']),
  note: z.string().max(1000).default(''),
  by: z.string(),
  at: z.string(),
});
export type ErrorLabel = z.infer<typeof ErrorLabel>;

/** Video check after rendering: technical checks in code plus a vision check on stills (binary items). */
export const VideoQa = z.object({
  passed: z.boolean(),
  checks: z.array(
    z.object({
      id: z.string(), // e.g. "resolution", "loudness", "text-readable", "person-in-hook", "placeholder-labelled"
      ok: z.boolean(),
      detail: z.string().max(500).default(''),
      frame: z.number().int().optional(),
    }),
  ),
  stillKeys: z.array(z.string()).default([]),
  checkedAt: z.string(),
});
export type VideoQa = z.infer<typeof VideoQa>;
